import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { Input } from "../../components/ui/input";
import { cn } from "../../components/ui/utils";
import {
  getNavCategories,
  canRenderNavCategory,
  filterNavLinks,
  NavLink,
} from "../../constants/navigation";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { useNavigationExtras } from "../../hooks/useNavigationExtras";
import { useStockYards } from "../../hooks/useStockYards";
import { useFrequentTiles } from "../../hooks/useFrequentTiles";
import { InstallAppBanner } from "../../components/InstallAppBanner";

// Accent color per navigation category. Full class strings (not composed)
// so Tailwind's compiler can see them.
interface TileColor {
  tile: string;
  icon: string;
}

const CATEGORY_COLORS: Record<string, TileColor> = {
  "MY PROFILE": {
    tile: "bg-blue-50 border-blue-100 hover:bg-blue-100 active:bg-blue-200/70 dark:bg-blue-900/20 dark:border-blue-800 dark:hover:bg-blue-900/40",
    icon: "text-blue-600 dark:text-blue-400",
  },
  HR: {
    tile: "bg-violet-50 border-violet-100 hover:bg-violet-100 active:bg-violet-200/70 dark:bg-violet-900/20 dark:border-violet-800 dark:hover:bg-violet-900/40",
    icon: "text-violet-600 dark:text-violet-400",
  },
  "SERVICE DESKS": {
    tile: "bg-cyan-50 border-cyan-100 hover:bg-cyan-100 active:bg-cyan-200/70 dark:bg-cyan-900/20 dark:border-cyan-800 dark:hover:bg-cyan-900/40",
    icon: "text-cyan-600 dark:text-cyan-400",
  },
  OPERATIONS: {
    tile: "bg-amber-50 border-amber-100 hover:bg-amber-100 active:bg-amber-200/70 dark:bg-amber-900/20 dark:border-amber-800 dark:hover:bg-amber-900/40",
    icon: "text-amber-600 dark:text-amber-400",
  },
  LISTS: {
    tile: "bg-emerald-50 border-emerald-100 hover:bg-emerald-100 active:bg-emerald-200/70 dark:bg-emerald-900/20 dark:border-emerald-800 dark:hover:bg-emerald-900/40",
    icon: "text-emerald-600 dark:text-emerald-400",
  },
  STOCK: {
    tile: "bg-teal-50 border-teal-100 hover:bg-teal-100 active:bg-teal-200/70 dark:bg-teal-900/20 dark:border-teal-800 dark:hover:bg-teal-900/40",
    icon: "text-teal-600 dark:text-teal-400",
  },
  SAFETY: {
    tile: "bg-rose-50 border-rose-100 hover:bg-rose-100 active:bg-rose-200/70 dark:bg-rose-900/20 dark:border-rose-800 dark:hover:bg-rose-900/40",
    icon: "text-rose-600 dark:text-rose-400",
  },
  IT: {
    tile: "bg-indigo-50 border-indigo-100 hover:bg-indigo-100 active:bg-indigo-200/70 dark:bg-indigo-900/20 dark:border-indigo-800 dark:hover:bg-indigo-900/40",
    icon: "text-indigo-600 dark:text-indigo-400",
  },
  FINANCE: {
    tile: "bg-orange-50 border-orange-100 hover:bg-orange-100 active:bg-orange-200/70 dark:bg-orange-900/20 dark:border-orange-800 dark:hover:bg-orange-900/40",
    icon: "text-orange-600 dark:text-orange-400",
  },
};

// Fallback for the top-level (uncategorized) group and future categories.
const DEFAULT_TILE_COLOR: TileColor = {
  tile: "bg-sky-50 border-sky-100 hover:bg-sky-100 active:bg-sky-200/70 dark:bg-sky-900/20 dark:border-sky-800 dark:hover:bg-sky-900/40",
  icon: "text-sky-600 dark:text-sky-400",
};

// Section headings ("MY PROFILE", "Stock Settings", "Most used"). One shared
// string so the grid, the sidebar-mirroring groups and the frequent row cannot
// drift apart.
const SECTION_HEADING =
  "px-1 mb-2 text-[11px] font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400";

/** One reachable destination, flattened out of the category/group tree for search and ranking. */
interface CatalogEntry {
  link: NavLink;
  color: TileColor;
  /** Owning category heading, e.g. "SAFETY"; empty for top-level items. */
  category: string;
  /** Sub-group heading, e.g. a stock yard name. */
  group?: string;
}

/**
 * m.idarah.io landing page — a grid of square app tiles replacing the
 * sidebar as the mobile entry point. Renders every module the current
 * user can access, using the shared navigation registry so visibility
 * rules always match the desktop sidebar.
 *
 * Field-first ordering: a search box and the destinations this handset
 * actually uses sit above the full directory, because a worker in gloves
 * should not have to scroll past Finance to reach Safety Observations.
 *
 * Group nodes (stock yards, Stock Settings, Closed Yards, Retired Desks)
 * become labelled sub-sections — the tree's nesting does not fit a flat
 * grid, and a bare "Returns" tile would not say which yard it belongs to.
 */
export const MobileHome: React.FC = () => {
  const { currentUser } = useAuth();
  const ability = useAbility(AbilityContext);
  const { activeDesks, retiredDesks, hasOnboarding } = useNavigationExtras();

  const canSeeStock = ability.can(ACTIONS.READ, RESOURCES.STOCK);
  const stockYards = useStockYards(canSeeStock);

  const [query, setQuery] = React.useState("");
  const { topHrefs, recordVisit } = useFrequentTiles(currentUser?.id);

  const navCategories = getNavCategories({
    currentUserId: currentUser?.id,
    activeDesks,
    retiredDesks,
    stockYards,
  });
  const filterCtx = { ability, currentUser, hasOnboarding };

  // Permission-filtered view of the registry. /dashboard is dropped: the grid
  // replaces the dashboard on m.idarah.io and /dashboard redirects back here,
  // so its tile would be a no-op.
  const visibleCategories = navCategories
    .filter(category => canRenderNavCategory(category, filterCtx))
    .map(category => ({
      ...category,
      links: filterNavLinks(category.links, filterCtx).filter(link => link.href !== "/dashboard"),
    }))
    .filter(category => category.links.length > 0);

  // Flat index of every destination the user can actually open. Group parents
  // are containers, not destinations, so only their children are indexed.
  const catalog: CatalogEntry[] = [];
  visibleCategories.forEach(category => {
    const color = CATEGORY_COLORS[category.category ?? ""] ?? DEFAULT_TILE_COLOR;
    category.links.forEach(link => {
      if (link.children?.length) {
        link.children.forEach(child =>
          catalog.push({ link: child, color, category: category.category ?? "", group: link.label })
        );
      } else {
        catalog.push({ link, color, category: category.category ?? "" });
      }
    });
  });

  const entryByHref: Record<string, CatalogEntry> = Object.fromEntries(
    catalog.map((entry): [string, CatalogEntry] => [entry.link.href, entry])
  );
  // Keeps the ranking order, and drops hrefs the user can no longer reach
  // (permissions changed since the tap was recorded).
  const frequent = topHrefs.map(href => entryByHref[href]).filter(Boolean);

  const trimmedQuery = query.trim().toLowerCase();
  const results = trimmedQuery
    ? catalog.filter(({ link, category, group }) =>
        [link.label, group ?? "", category].some(field =>
          field.toLowerCase().includes(trimmedQuery)
        )
      )
    : [];

  const renderTile = (link: NavLink, color: TileColor) => (
    <Link
      key={link.href + link.label}
      to={link.href}
      onClick={() => recordVisit(link.href)}
      className={cn(
        "flex min-h-28 flex-col items-center justify-center gap-2 rounded-xl border p-2.5 text-center shadow-sm",
        "touch-manipulation select-none transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        "active:scale-[0.97]",
        color.tile
      )}
    >
      <span aria-hidden className={cn("[&>svg]:h-7 [&>svg]:w-7", color.icon)}>{link.icon}</span>
      {/* 13px: the longest registry labels ("Direct Debit Register") must fit two
          lines in a third of a 360px viewport without ellipsis. */}
      <span className="text-[13px] font-medium leading-tight text-foreground line-clamp-2">
        {link.label}
      </span>
    </Link>
  );

  /**
   * The section's own cards go in one grid at the top; group sections (a stock yard,
   * Stock Settings, Closed Yards, Retired Desks) render below them. A group's cards come
   * straight after its heading, so a following card grid can never read as a child of the
   * group above it — Transfer Orders and Product Library belong to STOCK, not to a yard.
   * Groups keep their registry order relative to each other.
   */
  const renderBlocks = (links: NavLink[], color: TileColor) => {
    const cards = links.filter(link => !link.children);
    const groups = links.filter(link => link.children?.length);

    return (
      <>
        {cards.length > 0 && (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {cards.map(link => renderTile(link, color))}
          </div>
        )}
        {groups.map(link => (
          <div key={link.href + link.label} className="mt-3">
            <h3 className={cn(SECTION_HEADING, "flex items-center gap-2")}>
              <span className="truncate">{link.label}</span>
              {link.badge !== undefined && (
                <span className="shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  {link.badge}
                </span>
              )}
            </h3>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {(link.children ?? []).map(child => renderTile(child, color))}
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="mx-auto w-full max-w-2xl">
      <InstallAppBanner />
      <h1 className="px-1 pt-2 text-xl font-bold tracking-tight text-gray-900 dark:text-white">
        Hi {currentUser?.name?.split(" ")[0] || "there"}
      </h1>
      <p className="px-1 pb-3 text-sm text-gray-500 dark:text-gray-400">
        What would you like to do?
      </p>

      {/* Sticky so the directory below stays searchable without scrolling back up. */}
      <div className="sticky top-0 z-10 -mx-1 bg-white px-1 py-2 dark:bg-gray-900">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Escape") setQuery("");
            }}
            placeholder="Search apps and actions"
            aria-label="Search apps and actions"
            className="h-11 pr-9 pl-9 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {trimmedQuery ? (
        results.length > 0 ? (
          <ul className="mt-1 space-y-2">
            {results.map(({ link, color, category, group }) => {
              const breadcrumb = [group, category].filter(Boolean).join(" · ");
              return (
                <li key={link.href + link.label}>
                  <Link
                    to={link.href}
                    onClick={() => recordVisit(link.href)}
                    className={cn(
                      "flex min-h-14 items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm",
                      "touch-manipulation select-none transition",
                      "dark:border-gray-800 dark:bg-gray-900",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      "active:scale-[0.99] active:bg-gray-50 dark:active:bg-gray-800"
                    )}
                  >
                    <span aria-hidden className={cn("shrink-0 [&>svg]:h-5 [&>svg]:w-5", color.icon)}>
                      {link.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {link.label}
                      </span>
                      {breadcrumb && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {breadcrumb}
                        </span>
                      )}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-8 px-2 text-center text-sm text-muted-foreground">
            No apps or actions match “{query.trim()}”.
          </p>
        )
      ) : (
        <>
          {frequent.length > 0 && (
            <section className="mt-1">
              <h2 className={SECTION_HEADING}>Most used</h2>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {frequent.map(entry => renderTile(entry.link, entry.color))}
              </div>
            </section>
          )}

          {visibleCategories.map((category, index) => (
            <section key={category.category || `top-${index}`} className="mt-5 first:mt-0">
              {category.category && <h2 className={SECTION_HEADING}>{category.category}</h2>}
              {renderBlocks(
                category.links,
                CATEGORY_COLORS[category.category ?? ""] ?? DEFAULT_TILE_COLOR
              )}
            </section>
          ))}
        </>
      )}
    </div>
  );
};

export default MobileHome;
